import type { FC } from 'react';
import { Suspense } from 'react';
import { Route, Switch } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { routePaths } from 'src/services/routing/routePaths';
import type { AppRoute } from 'src/services/routing/routes';
import SectionProvider from 'src/components/app/context/SectionContext';
import PageFrame from 'src/components/common/PageFrame';
import PageNotFound from 'src/components/common/PageNotFound';
import LoadingPage from 'src/components/app/LoadingPage';

import PageTracking from './components/PageTracking';
import RouteGuard from './components/RouteGuard';

import 'src/sentryConfig';

export interface RoutesHandlerProps {
  routes: AppRoute[];
}

// Sentry - follows docs for React Router v5:
// https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/#react-router-v4v5
const SentryRoute = Sentry.withSentryRouting(Route);

const RoutesHandler: FC<RoutesHandlerProps> = ({ routes }) => (
  <Suspense
    fallback={
      <PageFrame>
        <LoadingPage />
      </PageFrame>
    }
  >
    <Switch>
      {routes.map(({ component: Component, path, section }, index) => (
        <SentryRoute
          exact
          key={index}
          path={path}
          render={(props) => (
            <RouteGuard section={section}>
              <SectionProvider section={section}>
                <PageTracking section={section} route={path} />
                {Component && <Component {...props} />}
              </SectionProvider>
            </RouteGuard>
          )}
        />
      ))}

      <SentryRoute
        path={routePaths.INDEX}
        component={() => (
          <PageFrame>
            <PageNotFound />
          </PageFrame>
        )}
      />
    </Switch>
  </Suspense>
);

export default RoutesHandler;
